import { Tooltip } from 'component-library';
import { comparisonColor, comparisonPositive } from '../../helpers/Custom';
import { Dashboard } from '../../models/ApiModels';
import ColumnChart from './ColumnChart';

interface Props {
  dashboardData: Dashboard;
  compare: boolean;
}

const DashboardDataOrders = (props: Props) => {
  const ytdOverviewToMap = [
    {
      label: 'Total upgrades YTD',
      className: 'prose-heading3',
      data: props.dashboardData.orders.totalUpgradesYTD,
      suffix: '',
    },
    {
      label: 'Total upgrade revenue YTD',
      className: 'prose-heading3 text-[#FFB900]',
      data: props.dashboardData.orders.orderRevenueYTD,
      suffix: 'KSEK',
    },
  ];

  const atOverviewToMap = [
    {
      label: 'Total upgrades AT',
      className: 'prose-heading3',
      data: props.dashboardData.orders.totalUpgradesAllTime,
      suffix: '',
    },
    {
      label: ' Total upgrade revenue AT',
      className: 'prose-heading3 text-[#FFB900]',
      data: props.dashboardData.orders.orderRevenueAllTime,
      suffix: 'KSEK',
    },
  ];

  const ytdListToMap = [
    {
      label: 'SEK',
      className: 'prose-heading4 text-[#CC9400]',
      data: props.dashboardData.orders.sekOrderRevenueYTD,
      suffix: 'SEK',
      hasTooltip: false
    },
    {
      label: 'USD',
      className: 'prose-heading4 text-[#CC9400]',
      data: props.dashboardData.orders.usdOrderRevenueYTD,
      suffix: '$',
      hasTooltip: false
    },
    {
      label: 'EUR',
      className: 'prose-heading4 text-[#CC9400]',
      data: props.dashboardData.orders.eurOrderRevenueYTD,
      suffix: '€',
      hasTooltip: false
    },
    {
      label: 'Potential Revenue',
      className: 'text-gray-80 flex whitespace-normal items-center gap-1',
      data: props.dashboardData.orders.potentialRevenue,
      suffix: 'SEK',
      hasTooltip: true
    },
  ];

  return (
    <div className='flex flex-col'>
      <div className='prose-heading4 mb-5 mt-10'>Upgrades</div>
      <div className='flex gap-4 flex-wrap'>
        <div className='bg-gray-3 pt-4 mb-4 md:mb-0 flex-1 w-full flex flex-col'>
          <div className='text-center mt-4 mb-6'>Upgrades year to date</div>
          <div className='flex flex-col gap-8 flex-1'>

            <div className='flex flex-col px-8'>
              <div>Revenue</div>
              <div className='mb-auto'>
                {ytdListToMap.map((item) => (
                  <div key={item.label}
                    className='flex items-center justify-between gap-8 whitespace-pre'>
                    <span className={item.className}>
                      {item.label}
                      {item.hasTooltip && <Tooltip description='The estimation is based on 2026 list prices for function packages, assuming the standard 40% discount we have. The current license distribution of the day for CRUISE and ULTIMATE is split on the created systems (that doesnt yet have a license), this gives an estimation of the coming revenue.' />}
                    </span>
                    <span className='flex gap-4 items-center'>
                      <span>
                        {item.data.count.toLocaleString('en-US')} {item.suffix}
                      </span>
                      {props.compare && (
                        <span className={`min-w-[5rem] ${comparisonColor(item.data.diff)}`}>
                          {comparisonPositive(item.data.diff) + item.data.diff.toLocaleString('en-US')}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className='w-full max-w-3xl mx-auto flex justify-center gap-8 md:gap-16 mt-auto pb-8 px-4'>
              {props.dashboardData &&
                ytdOverviewToMap.map((item) => {
                  const ytdRevenueInKRound = Math.round(item.data.count / 1000);
                  return (
                    <div key={item.label} className='text-center flex-1 min-w-0'>
                      <div>{item.label}</div>
                      <h3 className={item.className}>
                        {item.suffix === 'KSEK' ? ytdRevenueInKRound.toLocaleString('en-US') : item.data.count.toLocaleString('en-US')} {item.suffix}
                      </h3>
                      {props.compare && (
                        <span className={`prose-heading4 ${comparisonColor(item.data.diff)}`}>
                          {comparisonPositive(item.data.diff) + (item.suffix === 'KSEK' ? (item.data.diff / 1000).toLocaleString('en-US') : item.data.diff)} {item.suffix}
                        </span>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
        <div className='bg-gray-3 pt-4 mb-4 md:mb-0 flex-1 w-full flex flex-col'>
          <div className='text-center mt-4 mb-6'>Upgrades all time</div>
          <div className='flex flex-col flex-1'>
            <div className='flex flex-col px-8 flex-1 justify-center'>
              <ColumnChart
                colorSetting='yellow'
                series={props.dashboardData.orders.totalRevenuePerYear}
                compare={false}
              />
            </div>
            <div className='w-full max-w-3xl mx-auto flex justify-center gap-8 md:gap-16 mt-auto pb-8 px-4'>
              {props.dashboardData &&
                atOverviewToMap.map((item) => {
                  const atRevenueInKRound = Math.round(item.data.count / 1000);
                  return (
                    <div key={item.label} className='text-center flex-1 min-w-0'>
                      <div>{item.label}</div>
                      <h3 className={item.className}>
                        {item.suffix === 'KSEK' ? atRevenueInKRound.toLocaleString('en-US') : item.data.count.toLocaleString('en-US')} {item.suffix}
                      </h3>
                      {props.compare && (
                        <span className={`prose-heading4 ${comparisonColor(item.data.diff)}`}>
                          {comparisonPositive(item.data.diff) + (item.suffix === 'KSEK' ? (item.data.diff / 1000).toLocaleString('en-US') : item.data.diff)} {item.suffix}
                        </span>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardDataOrders;
